import Box from '@mui/material/Box';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useGetWorkspacesInfiniteQuery } from 'src/apiClients';
import type { QueryWhereValue, WorkspaceListItem } from 'src/apiClients/queries';
import type { DataTableColDef, DataTableRow } from 'src/components/data-table/DataTable.types';
import { SortDirection } from 'src/components/data-table/DataTable.types';
import { EntityListPage } from 'src/components/entity-list-page';
import { i18n as routesI18n } from 'src/hooks/use-imago-mp-routes.i18n';

import { useInstance } from '../../InstanceContext';
import { SectionHeading } from '../SectionHeading';
import { createWorkspaceColumns, mapWorkspaceRow } from './workspace-table-formatters';
import { workspaceTableI18n } from './workspace-table-formatters.i18n';
import { WorkspaceDatasetCountChip } from './WorkspaceDatasetCountChip';
import { WorkspaceDatasets } from './WorkspaceDatasets';
import { i18n } from './WorkspacesSection.i18n';
import { WorkspaceUserCountChip } from './WorkspaceUserCountChip';
import { WorkspaceUsers } from './WorkspaceUsers';

const SEARCH_COLUMNS = ['name', 'id'];
const INITIAL_SORT = [
    { field: 'state', direction: SortDirection.Asc },
    { field: 'name', direction: SortDirection.Asc },
];

export const WorkspacesSection = () => {
    const intl = useIntl();
    const { subscription } = useInstance();
    const [total, setTotal] = useState<number | undefined>(undefined);

    const baseWhere = useMemo<Record<string, QueryWhereValue>>(
        () => ({ subscriptionId: subscription.id }),
        [subscription.id],
    );

    const baseExtraParams = useMemo(
        () => ({ tenantId: subscription.tenantId }),
        [subscription.tenantId],
    );

    const columns = useMemo<DataTableColDef[]>(() => {
        const [nameCol, descriptionCol, ...rest] = createWorkspaceColumns(intl);
        const datasetsColumn: DataTableColDef = {
            field: 'datasets',
            headerName: intl.formatMessage(workspaceTableI18n.datasets),
            width: 80,
            sortable: false,
            renderCell: (row) => (
                <WorkspaceDatasetCountChip
                    workspaceId={row.id as string}
                    tenantId={subscription.tenantId}
                />
            ),
        };
        const usersColumn: DataTableColDef = {
            field: 'users',
            headerName: intl.formatMessage(workspaceTableI18n.users),
            width: 80,
            sortable: false,
            renderCell: (row) => (
                <WorkspaceUserCountChip
                    workspaceId={row.id as string}
                    tenantId={subscription.tenantId}
                />
            ),
        };
        return [nameCol, descriptionCol, datasetsColumn, usersColumn, ...rest];
    }, [intl, subscription.tenantId]);

    const renderExpandedRow = useCallback(
        (row: DataTableRow) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <WorkspaceUsers workspaceId={row.id as string} tenantId={subscription.tenantId} />
                <WorkspaceDatasets
                    workspaceId={row.id as string}
                    tenantId={subscription.tenantId}
                />
            </Box>
        ),
        [subscription.tenantId],
    );

    const handleTotalChange = useCallback((next: number) => setTotal(next), []);

    return (
        <>
            <SectionHeading
                title={intl.formatMessage(routesI18n.instanceSectionWorkspaces)}
                count={total}
                automationId="instance-workspaces-count"
            />
            <EntityListPage<WorkspaceListItem>
                automationId="instance-workspaces-section"
                useQuery={useGetWorkspacesInfiniteQuery}
                searchColumns={SEARCH_COLUMNS}
                initialSort={INITIAL_SORT}
                columns={columns}
                mapRow={mapWorkspaceRow}
                baseWhere={baseWhere}
                baseExtraParams={baseExtraParams}
                disableScrollbarGutter
                renderExpandedRow={renderExpandedRow}
                onTotalChange={handleTotalChange}
                titleWhenNoResults={intl.formatMessage(workspaceTableI18n.noWorkspacesTitle)}
                messageWhenNoResults={intl.formatMessage(i18n.noWorkspacesMessage)}
                titleWhenLoadError={intl.formatMessage(workspaceTableI18n.loadErrorTitle)}
                messageWhenLoadError={intl.formatMessage(workspaceTableI18n.loadErrorMessage)}
            />
        </>
    );
};
